/* eslint-disable no-param-reassign */
import createBaseSurveyMeta from './_BASE';
import colors from '../../Colors';

import {
	computeUuid,
	computeFormattedStationId,
	mapGpsDistance,
	computeDistance,
} from '../util/readings.parse.computeProps';

import { defaultCreateSimplifiedReadings } from '../util/general';
import { createComputeYMinYMax } from '../util/yMinYMax';
import {
	createReadingKeyProps,
	createGetReadingKeys,
	createGetReadingKeyProps,
	createGetColumns,
	createGetSurveyColor,
	createTransformToCsvJson
} from './util';
import { getSystemOfUnits } from '../../../../util/user';

const INVERT_Y_AXIS = false;

const isDefined = v => v !== undefined && v !== null;

// eslint-disable-next-line no-unused-vars
export default _survey => {
	const yAxisUnitDisplayText = (getSystemOfUnits() === 'Imperial') ? 'feet' : 'meters';
	// CONSTANTS

	const gapThreshold = 200;

	// READING KEYS

	const _colors = [colors.getColor(), colors.getColor(), colors.getColor(), colors.getColor(), colors.getColor()];
	const readingKeys = [
		{
			label: 'Bottom of Pipe',
			value: 'bopelv',
			seriesType: 'line',
			color: _colors[0],
			yAxisUnitDisplayText
		},
		{
			label: 'Center of Pipe',
			value: 'copelv',
			seriesType: 'line',
			color: _colors[1],
			yAxisUnitDisplayText
		},
		{
			label: 'Top of Pipe',
			value: 'topelv',
			seriesType: 'line',
			color: _colors[2],
			yAxisUnitDisplayText
		},
		{
			label: 'Water Elevation',
			value: 'wtrelv',
			seriesType: 'line',
			color: _colors[3],
			yAxisUnitDisplayText
		},
		{
			label: 'Ground Elevation',
			value: 'elevation',
			seriesType: 'line',
			color: _colors[4],
			borderColor: _colors[4],
			backgroundColor: `${_colors[4]}30`,
			fill: "origin",		
			yAxisUnitDisplayText
		},	
	];
	const readingKeyProps = createReadingKeyProps(readingKeys);
	const computeYMinYMax = createComputeYMinYMax(readingKeys);

	// SKIP READING

	const isSkipReading = reading =>
		reading && readingKeys.every(rk => reading[rk.value] === 0);

	// HAS GAP

	const hasGap = (currReading, prevReading) => {
		return !!prevReading && currReading.computedDistance - prevReading.id > gapThreshold;
	};

	// INTERPOLATE

	const interpolate = (
		// eslint-disable-next-line no-unused-vars
		_prevReading,
		// eslint-disable-next-line no-unused-vars
		_nextReading,
		// eslint-disable-next-line no-unused-vars
		_alignToReading
	) => {};

	// COMPUTE PROPS - to be used by readings.parse

	const computeHilo = r => {
		r.low = r.low || 0;
	};

	const computeProps = (survey, _prevReading, currReading, currIdx) => {
		computeUuid(currReading, currIdx);
		computeFormattedStationId(currReading);
		computeHilo(currReading);
		computeYMinYMax(survey, currReading);
		mapGpsDistance(currReading);
		computeDistance(_prevReading, currReading);
	};

	// SIMPLIFIED READINGS - creates a smaller data set to be used for rendering polyline on map
	const createSimplifiedReadings = defaultCreateSimplifiedReadings;

	const filterReadingsOfUnsupportedSurveyType = readings => {
		if (!readings) {
			return readings;
		}

		return readings.filter(r => r.ir_pct !== null && r.ir_pct !== undefined);
	};

	const columns = [
		{ label: 'ID', value: 'id', checked: true, width: 48, required: true },
		{
			label: 'Station',
			value: 'formattedDst',
			checked: true,
			width: 80,
			showInTooltip: false
		},
		{ label: 'GRD', value: 'elevation', checked: true, width: 44 },
		{ label: 'WTR', value: 'wtrelv', checked: true, width: 44 },
		{ label: 'TOP', value: 'topelv', checked: true, width: 44 },
		{ label: 'COP', value: 'copelv', checked: true, width: 44 },
		{ label: 'BOP', value: 'bopelv', checked: true, width: 44 },
		{
			label: 'LAT',
			value: 'lat',
			checked: true,
			width: 65,
			getCsvValue: r => {
				if (isDefined(r.lat)) {
					return r.lat;
				}
				if (isDefined(r.loc)) {
					return r.loc[0];
				}
				return undefined;
			}
		},
		{
			label: 'LNG',
			value: 'lng',
			checked: true,
			width: 70,
			getCsvValue: r => {
				if (isDefined(r.lng)) {
					return r.lng;
				}
				if (isDefined(r.loc)) {
					return r.loc[1];
				}
				return undefined;
			}
		},
		{
			label: 'COMMENTS',
			value: 'cmt',
			checked: true,
			width: 85,
			type: 'text'
		}
	];

	// MAIN
	const getReadingKeys = createGetReadingKeys(readingKeys);
	const getColumns = createGetColumns(columns);
	const transformToCsvJson = createTransformToCsvJson(getColumns);

	const meta = {
		...createBaseSurveyMeta(_survey),
		isChartInversed: INVERT_Y_AXIS,
		getReadingKeys,
		getReadingKeyProps: createGetReadingKeyProps(readingKeyProps),
		getColumns,
		transformToCsvJson,
		getSurveyColor: createGetSurveyColor(getReadingKeys),
		fkey: 'topelv',
		isSkipReading,
		hasGap,
		interpolate,
		computeProps,
		computeYMinYMax,
		createSimplifiedReadings,
		filterReadingsOfUnsupportedSurveyType
	};

	return meta;
};
